import { Box, Card, Chip, Grid, Typography, useTheme, Avatar, LinearProgress, TextField, MenuItem, IconButton, Tooltip } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { DateRange, ButtonBasic } from "../../components";
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CableIcon from '@mui/icons-material/Cable';
import ApiIcon from '@mui/icons-material/Api';
import PersonIcon from '@mui/icons-material/Person';
import TableChartIcon from '@mui/icons-material/TableChart';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AnaliticaEnlacesService from "../../services/analiticaEnlaces.service";
import FechaFunction from "../../function/FechaFunction";

/* ----- Card KPI ----- */
const KpiCard = ({ title, value, icon, color, subtitle, isDarkMode }) => (
    <Card sx={{
        p: 2.5,
        borderRadius: 3,
        background: isDarkMode ? '#2d3a52' : '#fff',
        boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#90a4ae' : '#666', fontSize: 12, mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant="h4" sx={{ color: isDarkMode ? '#e0e0e0' : '#1a1a1a', fontWeight: 'bold' }}>
                    {value}
                </Typography>
            </Box>
            <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${color}20`
            }}>
                {icon}
            </Box>
        </Box>
        {subtitle && (
            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999', mt: 1 }}>
                {subtitle}
            </Typography>
        )}
    </Card>
);

/* ----- Ranking Row ----- */
const RankingRow = ({ label, value, total, color, isDarkMode }) => {
    const porc = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" sx={{ fontSize: 12, color: isDarkMode ? '#c9d1e0' : '#333', fontWeight: 500 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 12, color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 700 }}>
                    {value} <span style={{ opacity: 0.6 }}>({porc}%)</span>
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={porc}
                sx={{
                    height: 6, borderRadius: 3,
                    background: isDarkMode ? '#1a2237' : '#f0f0f0',
                    '& .MuiLinearProgress-bar': { background: color }
                }}
            />
        </Box>
    );
};

const AnaliticaEnlacesScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    // ── Estado ────────────────────────────────────────────────────
    const [loading, setLoading]   = useState(false);
    const [data, setData]         = useState(null);
    const [usuarios, setUsuarios] = useState([]);

    // Filtros
    const [fechaSelect, setFechaSelect] = useState(() => {
        const hoy = new Date();
        const hace7 = new Date();
        hace7.setDate(hoy.getDate() - 7);
        return { startDate: hace7, endDate: hoy };
    });
    const [apiFilter, setApiFilter]       = useState('');
    const [userFilter, setUserFilter]     = useState('');
    const [search, setSearch]             = useState('');

    // Paginación
    const [page, setPage]         = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // ── Cargar usuarios (para el filtro) al montar ──────────────────
    useEffect(() => {
        AnaliticaEnlacesService.getUsuariosEnlaces()
            .then((res) => setUsuarios(res?.data || []))
            .catch(console.error);
    }, []);

    // ── Ejecutar búsqueda ──────────────────────────────────────────
    const buscar = (opts = {}) => {
        setLoading(true);
        const body = {
            fecIni: FechaFunction.format(fechaSelect.startDate, 'yyyy-MM-dd'),
            fecFin: FechaFunction.format(fechaSelect.endDate,   'yyyy-MM-dd'),
            api:       apiFilter || undefined,
            usuarioId: userFilter || undefined,
            search:    search || undefined,
            page:      opts.page     ?? page,
            pageSize:  opts.pageSize ?? pageSize,
        };
        AnaliticaEnlacesService.getDashboard(body)
            .then((res) => setData(res?.data || null))
            .catch((err) => { console.error(err); setData(null); })
            .finally(() => setLoading(false));
    };

    // Búsqueda inicial
    useEffect(() => { buscar({ page: 1 }); /* eslint-disable-next-line */ }, []);

    // Cuando cambian filtros vía botón: reset page a 1
    const onBuscarClick = () => {
        setPage(1);
        buscar({ page: 1 });
    };

    // Limpiar filtros
    const onLimpiarFiltros = () => {
        const hoy = new Date();
        const hace7 = new Date();
        hace7.setDate(hoy.getDate() - 7);
        setFechaSelect({ startDate: hace7, endDate: hoy });
        setApiFilter('');
        setUserFilter('');
        setSearch('');
        setPage(1);
    };

    // Cambio de página
    const cambiarPagina = (nuevaPage) => {
        setPage(nuevaPage);
        buscar({ page: nuevaPage });
    };
    const cambiarPageSize = (nuevoSize) => {
        setPageSize(nuevoSize);
        setPage(1);
        buscar({ page: 1, pageSize: nuevoSize });
    };

    // ── Colores por API ────────────────────────────────────────────
    const apiColors = useMemo(() => ({
        '/login':            '#3b82f6',
        '/service/status':   '#10b981',
        '/service/coverage': '#f59e0b',
        '/service/dni':      '#8b5cf6',
        '/list/plans':       '#ec4899',
    }), []);
    const getApiColor = (api) => apiColors[api] || '#64748b';

    const metrics    = data?.metrics    || null;
    const items      = data?.items      || [];
    const pagination = data?.pagination || { page: 1, pageSize: 10, total: 0, totalPages: 0 };

    // ── UI ──────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CableIcon sx={{ color: isDarkMode ? '#90caf9' : '#1976d2', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: isDarkMode ? '#e0e0e0' : '#1a1a1a' }}>
                    Analítica de Enlaces
                </Typography>
            </Box>

            {/* ── FILTROS ─────────────────────────────────────────── */}
            <Card sx={{
                p: 2.5, mb: 2, borderRadius: 3,
                background: isDarkMode ? '#2d3a52' : '#fff',
                boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            }}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={3}>
                        <Typography variant="caption" sx={{
                            color: isDarkMode ? '#90a4ae' : '#666',
                            mb: 1, display: 'block', fontWeight: 600, fontSize: 12,
                            textTransform: 'uppercase', letterSpacing: 0.5
                        }}>
                            Rango de fechas
                        </Typography>
                        <DateRange
                            minDate={new Date('2024-01-01')}
                            initianDate={fechaSelect}
                            onChange={setFechaSelect}
                            maxDays={365}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="API"
                            value={apiFilter}
                            onChange={(e) => setApiFilter(e.target.value)}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            <MenuItem value="/login">/login</MenuItem>
                            <MenuItem value="/service/status">/service/status</MenuItem>
                            <MenuItem value="/service/coverage">/service/coverage</MenuItem>
                            <MenuItem value="/service/dni">/service/dni</MenuItem>
                            <MenuItem value="/list/plans">/list/plans</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Usuario"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {usuarios.map((u) => (
                                <MenuItem key={u.usuarioId} value={u.usuarioId}>
                                    {u.nombre}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <Box display="flex" gap={1}>
                            <ButtonBasic
                                textButton="Buscar"
                                variant='wk'
                                color='nextStep'
                                iconStart={<SearchIcon />}
                                onClick={onBuscarClick}
                            />
                            <ButtonBasic
                                textButton="Limpiar"
                                variant='wk'
                                color='cancel'
                                iconStart={<ClearAllIcon />}
                                onClick={onLimpiarFiltros}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            {loading && (
                <LinearProgress sx={{ mb: 2, borderRadius: 3 }} />
            )}

            {/* ── KPIs ─────────────────────────────────────────────── */}
            {metrics && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Total de llamadas"
                            value={metrics.total.toLocaleString()}
                            icon={<ApiIcon sx={{ color: '#3b82f6' }} />}
                            color="#3b82f6"
                            subtitle={`Rango: ${FechaFunction.format(fechaSelect.startDate, 'dd/MM/yyyy')} - ${FechaFunction.format(fechaSelect.endDate, 'dd/MM/yyyy')}`}
                            isDarkMode={isDarkMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="APIs distintas"
                            value={metrics.porApi?.length || 0}
                            icon={<CableIcon sx={{ color: '#10b981' }} />}
                            color="#10b981"
                            subtitle="Endpoints utilizados"
                            isDarkMode={isDarkMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Usuarios activos"
                            value={metrics.porUsuario?.length || 0}
                            icon={<PersonIcon sx={{ color: '#8b5cf6' }} />}
                            color="#8b5cf6"
                            subtitle="Con actividad en el rango"
                            isDarkMode={isDarkMode}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <KpiCard
                            title="Promedio diario"
                            value={metrics.porDia?.length ? Math.round(metrics.total / metrics.porDia.length).toLocaleString() : 0}
                            icon={<TableChartIcon sx={{ color: '#f59e0b' }} />}
                            color="#f59e0b"
                            subtitle="Llamadas / día"
                            isDarkMode={isDarkMode}
                        />
                    </Grid>
                </Grid>
            )}

            {/* ── RANKINGS: API + Usuarios ─────────────────────────── */}
            {metrics && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            p: 2.5, borderRadius: 3,
                            background: isDarkMode ? '#2d3a52' : '#fff',
                            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%'
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: isDarkMode ? '#e0e0e0' : '#1a1a1a' }}>
                                Uso por API
                            </Typography>
                            {(metrics.porApi || []).length === 0 && (
                                <Typography variant="body2" sx={{ color: isDarkMode ? '#78909c' : '#999', textAlign: 'center', py: 3 }}>
                                    Sin datos
                                </Typography>
                            )}
                            {(metrics.porApi || []).map((row) => (
                                <RankingRow
                                    key={row.api || 'unknown'}
                                    label={row.api || '— sin api —'}
                                    value={row.total}
                                    total={metrics.total || 1}
                                    color={getApiColor(row.api)}
                                    isDarkMode={isDarkMode}
                                />
                            ))}
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            p: 2.5, borderRadius: 3,
                            background: isDarkMode ? '#2d3a52' : '#fff',
                            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                            height: '100%'
                        }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: isDarkMode ? '#e0e0e0' : '#1a1a1a' }}>
                                Top usuarios
                            </Typography>
                            {(metrics.porUsuario || []).length === 0 && (
                                <Typography variant="body2" sx={{ color: isDarkMode ? '#78909c' : '#999', textAlign: 'center', py: 3 }}>
                                    Sin datos
                                </Typography>
                            )}
                            {(metrics.porUsuario || []).map((u) => (
                                <Box key={u.usuarioId} display="flex" alignItems="center" gap={1.5} mb={1.2}>
                                    <Avatar
                                        src={u.foto || undefined}
                                        sx={{ width: 32, height: 32, fontSize: 13, bgcolor: '#8b5cf6' }}
                                    >
                                        {u.nombre?.charAt(0)?.toUpperCase() || 'U'}
                                    </Avatar>
                                    <Box flex={1}>
                                        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, color: isDarkMode ? '#c9d1e0' : '#333' }}>
                                            {u.nombre}
                                        </Typography>
                                        {u.departamento && (
                                            <Typography variant="caption" sx={{ fontSize: 11, color: isDarkMode ? '#78909c' : '#999' }}>
                                                {u.departamento}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip
                                        label={u.total.toLocaleString()}
                                        size="small"
                                        sx={{
                                            bgcolor: isDarkMode ? '#3d4a62' : '#eef2ff',
                                            color:   isDarkMode ? '#93c5fd' : '#3d63dd',
                                            fontWeight: 700,
                                            fontSize: 12
                                        }}
                                    />
                                </Box>
                            ))}
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* ── TABLA PAGINADA ─────────────────────────────────── */}
            <Card sx={{
                p: 2.5, borderRadius: 3, mb: 10,
                background: isDarkMode ? '#2d3a52' : '#fff',
                boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDarkMode ? '#e0e0e0' : '#1a1a1a' }}>
                        Registros ({pagination.total.toLocaleString()})
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                            select
                            size="small"
                            label="Filas"
                            value={pageSize}
                            onChange={(e) => cambiarPageSize(parseInt(e.target.value, 10))}
                            sx={{ minWidth: 90 }}
                        >
                            {[10, 25, 50, 100].map((n) => (
                                <MenuItem key={n} value={n}>{n}</MenuItem>
                            ))}
                        </TextField>
                        <Tooltip title="Refrescar">
                            <IconButton size="small" onClick={() => buscar()}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ overflow: 'auto' }} className='side'>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: isDarkMode ? '#1a2237' : '#f5f7fa' }}>
                                <th style={thStyle(isDarkMode)}>Fecha</th>
                                <th style={thStyle(isDarkMode)}>Usuario</th>
                                <th style={thStyle(isDarkMode)}>API</th>
                                <th style={thStyle(isDarkMode)}>Tabla</th>
                                <th style={thStyle(isDarkMode)}>Nro Registro</th>
                                <th style={thStyle(isDarkMode)}>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: isDarkMode ? '#78909c' : '#999' }}>
                                        {loading ? 'Cargando…' : 'Sin registros para los filtros seleccionados.'}
                                    </td>
                                </tr>
                            )}
                            {items.map((it) => (
                                <tr key={it.id}>
                                    <td style={tdStyle(isDarkMode)}>
                                        {it.createdAt ? FechaFunction.format(new Date(it.createdAt), 'dd/MM/yyyy HH:mm') : '-'}
                                    </td>
                                    <td style={tdStyle(isDarkMode)}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar src={it.foto || undefined} sx={{ width: 24, height: 24, fontSize: 11 }}>
                                                {it.usuarioNombre?.charAt(0)?.toUpperCase() || 'U'}
                                            </Avatar>
                                            <span>{it.usuarioNombre}</span>
                                        </Box>
                                    </td>
                                    <td style={tdStyle(isDarkMode)}>
                                        <Chip
                                            label={it.api || '-'}
                                            size="small"
                                            sx={{
                                                bgcolor: `${getApiColor(it.api)}20`,
                                                color: getApiColor(it.api),
                                                fontWeight: 600,
                                                fontSize: 11,
                                                height: 22,
                                            }}
                                        />
                                    </td>
                                    <td style={tdStyle(isDarkMode)}>{it.tablaRegistro || '-'}</td>
                                    <td style={tdStyle(isDarkMode)}>{it.nroRegistro || '-'}</td>
                                    <td style={{ ...tdStyle(isDarkMode), maxWidth: 320 }}>
                                        <span title={it.descripcion}>{it.descripcion || '-'}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>

                {/* Paginación */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>
                        Página {pagination.page} de {pagination.totalPages || 1} — {pagination.total.toLocaleString()} registros
                    </Typography>
                    <Box display="flex" gap={0.5}>
                        <IconButton size="small" disabled={pagination.page <= 1 || loading} onClick={() => cambiarPagina(1)}>
                            <FirstPageIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={pagination.page <= 1 || loading} onClick={() => cambiarPagina(pagination.page - 1)}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => cambiarPagina(pagination.page + 1)}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => cambiarPagina(pagination.totalPages)}>
                            <LastPageIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};

const thStyle = (isDarkMode) => ({
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: isDarkMode ? '#90a4ae' : '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    borderBottom: `2px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
    position: 'sticky',
    top: 0,
    background: isDarkMode ? '#1a2237' : '#f5f7fa',
    zIndex: 1,
});

const tdStyle = (isDarkMode) => ({
    padding: '8px 12px',
    fontSize: 12.5,
    color: isDarkMode ? '#c9d1e0' : '#333',
    borderBottom: `1px solid ${isDarkMode ? '#2a3548' : '#f0f0f0'}`,
    verticalAlign: 'middle',
});

export default AnaliticaEnlacesScreen;
